<template>
  <div class="view-container">
    <div class="view-gabarit">
      <form autocomplete="off" @submit.prevent="submitRegionsAndProceedStep">
        <div class="view-row">
          <div class="view-description">
            <p class="baseline">{{ $t('studyCreation.excludeAddress') }}</p>
            <p class="description">
              {{ $t('studyCreation.excludeAddressDescription') }}
            </p>
            <p class="description">
              <b>{{ $t('studyCreation.excludeAddressZone') }}</b
              ><br />{{ $t('studyCreation.excludeAddressZoneDescription') }}
            </p>
            <div class="counter-container">
              <div class="counter">
                <AnimatedValue class="active" :value="excludedAddresses" />
                <span class="divider">/</span>
                <span class="total">{{ totalAddresses }}</span>
              </div>
              <div class="text">
                {{ $t('studyCreation.excludedAddress', excludedAddresses) }}
              </div>
            </div>
            <Transition name="fade" mode="out-in">
              <div v-if="isCarOnly" class="error-container">
                <div class="icon-container">
                  <i class="icon-alert"></i>
                </div>
                <div class="error-info">
                  <div class="error-title">
                    {{ $t('studyCreation.carOnlyTitle') }}
                  </div>
                  <div class="error-detail">
                    {{ $t('studyCreation.carOnlyDetail') }}
                  </div>
                </div>
              </div>
            </Transition>
          </div>
          <div class="view-content">
            <button class="study-button main export-button" @click="importCsvList">{{ $t('export') }}<i class="icon-download"></i></button>
            <div v-for="(countryRegions, country) in countries" :key="country" class="country">
              <div class="country-title">{{ country }}</div>
              <fieldset v-for="(region, idx) in countryRegions" :key="idx">
                <input
                  :id="`checkbox-${region.name}`"
                  v-model="region.isNotExcluded"
                  :name="`checkbox-${region.name}`"
                  type="checkbox"
                  :class="{ disabled: region.isNotOfflimit }"
                />
                <label :for="`checkbox-${region.name}`" :class="{ checked: region.isNotExcluded }"
                  >{{ region.name }}
                  <span v-if="region.name" class="region-count">({{ region.count }})</span>
                  <span v-if="!region.isNotOfflimit || !region.name" class="warning-tag">
                    {{ $t('outside') }}
                  </span>
                </label>
                <div
                  v-if="region.offLimitAddresses.length"
                  class="off-limit-addresses-container"
                  :class="{ open: openedRegion.includes(region.name) }"
                  :style="{ height: `${(region.offLimitAddresses.length + 1) * 18}px` }"
                  @click="toggleRegion(region)"
                >
                  <div class="off-limit-addresses-title">
                    {{
                      $t('studyCreation.offLimitAddresses', region.offLimitAddresses.length, {
                        count: region.offLimitAddresses.length,
                      })
                    }}
                    <i class="icon-arrow_down"></i>
                  </div>
                  <div v-for="address in region.offLimitAddresses" :key="address.formatted" class="off-limit-address">
                    {{ address.formatted }} <span class="coverage">({{ address.coverage }})</span>
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
        </div>
        <div class="view-row">
          <div class="view-description"></div>
          <div class="view-content">
            <fieldset>
              <button :class="{ disabled: regions.length === 0 }" class="study-button main" type="submit">
                {{ $t('validate') }}
              </button>
            </fieldset>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import AnimatedValue from '@/components/AnimatedValue';

export default {
  components: {
    AnimatedValue,
  },

  data() {
    return {
      fetching: false,
      status: null,
      code: null,
      regions: [],
      openedRegion: [],
    };
  },

  computed: {
    countries() {
      return this.regions.reduce((acc, el) => {
        (acc[el['country']] = acc[el['country']] || []).push(el);
        return acc;
      }, {});
    },

    totalAddresses() {
      return this.regions.reduce((acc, val) => acc + val.count, 0);
    },

    excludedAddresses() {
      return this.regions.filter(i => !i.isNotExcluded).reduce((acc, val) => acc + val.count, 0);
    },

    isCarOnly() {
      return this.regions.filter(i => i.isNotExcluded && !i.isNotOfflimit).length;
    },
  },

  mounted() {
    this.fetching = true;
    this.fetchProcess();
  },

  beforeUnmount() {
    if (this.setTimeout) {
      clearTimeout(this.setTimeout);
    }
  },

  methods: {
    fetchProcess() {
      if (this._isBeingDestroyed || this._isDestroyed) {
        if (this.setTimeout) {
          clearTimeout(this.setTimeout);
        }
        return;
      }
      const id = this.$route.params.studyId;
      if (id) {
        this.$store
          .dispatch('fetchProcessStatus', {
            id,
          })
          .then(response => {
            if (response.code !== 6 && response.code !== 7 && response.code !== 16) {
              this.setTimeout = setTimeout(() => this.fetchProcess(), 1000);
              return;
            } else if (response.code === 14) {
              alert('Une erreur est survenue');
              this.$router.push({ name: 'studyList' });
            }

            if (this._isBeingDestroyed || this._isDestroyed) {
              if (this.setTimeout) {
                clearTimeout(this.setTimeout);
              }
              return;
            }
            const { code, value } = response;
            this.status = value;
            this.code = code;

            this.getRegion(id);
          });
      }
    },

    getRegion(id) {
      this.$store
        .dispatch('getStudyRegions', {
          id,
        })
        .then(result => result.data)
        .then(data => {
          this.regions = data.regions.map(item => {
            return {
              country: item.country,
              name: item.name,
              count: item.count,
              isNotExcluded: !item.is_excluded,
              isNotOfflimit: !item.is_off_limit,
              offLimitAddresses: item.off_limit_addresses,
            };
          });
        });
    },

    async submitRegionsAndProceedStep() {
      this.submitRegions().then(() => {
        this.$router.push({ name: 'studyEditDetails' });
      });
    },

    async submitRegions() {
      const regions = this.regions.reduce((acc, region) => {
        if (region.name) {
          acc.push({
            name: region.name,
            exclude: !region.isNotExcluded,
          });
        }
        return acc;
      }, []);

      const data = {
        id: this.$route.params.studyId,
        payload: {
          regions,
        },
      };

      await this.$store.dispatch('patchStudyRegions', data);
    },

    toggleRegion(region) {
      this.openedRegion.includes(region.name) ? this.openedRegion.splice(this.openedRegion.indexOf(region.name), 1) : this.openedRegion.push(region.name);
    },

    async importCsvList(e) {
      e.preventDefault();

      await this.submitRegions();

      const csvData = await this.$store.dispatch('exportCollabCsv', { studyId: this.$route.params.studyId });
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8,' });
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.setAttribute('href', blobUrl);
      link.setAttribute('download', `${this.$route.params.studyId}.csv`);
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';

.counter-container {
  height: 80px;
  padding: 8px 16px;
  margin: 24px 0 0;
  border: solid 1px colors.$clear-border-color;
  border-right: 0;
  border-left: 0;
  display: flex;
  align-items: center;

  .counter {
    flex-shrink: 0;
    font-size: 48px;
    margin-right: 32px;
    font-family: 'Golden Plains';

    .active {
      display: inline-block;
      width: 64px;
      text-align: right;
      color: colors.$main-color;
    }
    .divider {
      color: colors.$earth-color;
      margin: 0 8px;
    }
    .total {
      color: colors.$dark-color;
    }
  }

  .text {
  }
}

label {
  padding-bottom: 4px;
  overflow: visible;
}

.warning-tag {
  background-color: #bc141a;
  font-size: 11px;
  color: white;
  border-radius: 2px;
  padding: 2px 6px;
  margin-left: 4px;
}

.checked {
  color: colors.$navy-color;
}

.region-count {
  font-weight: 300;
  opacity: 0.6;
}

.off-limit-addresses-container {
  font-size: 12px;
  margin-top: 8px;
  margin-bottom: 12px;
  overflow: hidden;
  transition: 0.2s;

  &:not(.open) {
    height: 16px !important;
  }

  &.open {
    .off-limit-addresses-title {
      i {
        transform: rotate(0deg);
      }
    }
  }

  .off-limit-addresses-title {
    display: flex;
    align-items: center;
    font-weight: bold;
    height: 16px;
    cursor: pointer;

    i {
      transform: rotate(180deg);
      transition: 0.2s;
    }

    &:hover {
      i {
        color: colors.$main-color;
      }
    }
  }

  .off-limit-address {
    display: flex;
    align-items: center;
    color: #3c3c3b;
    height: 16px;
    margin-bottom: 2px;

    .coverage {
      margin-left: 2px;
      opacity: 0.6;
    }

    &::before {
      content: '\e822';
      font-family: 'jllcommuter';
      color: colors.$red-color;
    }
  }
}

.error-container {
  width: 100%;
  margin: 16px auto;
  padding: 8px 16px 12px;
  overflow: hidden;
  display: flex;
  color: colors.$red-color;
  background: rgba(colors.$red-color, 0.1);
  border: solid 2px colors.$red-color;
  border-radius: 4px;
  box-sizing: border-box;
  transition: 0.4s ease;

  .icon-container {
    margin: 0 8px 0 0;
    font-size: 16px;
  }

  .error-title {
    font-weight: 600;
    font-size: 16px;
  }

  .error-detail {
    font-size: 14px;
  }
}

.export-button {
  margin-left: auto;
  scale: 0.9;
  margin: 0;
  margin-left: auto;
  .icon-download {
    font-size: 1rem;
    float: right;
    cursor: pointer;
    &:hover {
      color: colors.$main-color;
    }
  }
}

.country {
  .country-title {
    max-width: 370px;
    margin: 16px auto 12px;
    font-size: 18px;
    font-weight: bold;
  }
}
</style>
